import React from 'react';
import { 
  Tabs, 
  Tab, 
  Box, 
  useTheme, 
  alpha 
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * AnimatedTabs - Enhanced tabs component with smooth animations
 * Provides modern styling and smooth transitions between tabs
 */
const AnimatedTabs = ({
  tabs = [],
  value = 0,
  onChange,
  variant = "standard",
  color = "primary",
  size = "medium",
  sx = {},
  ...props 
}) => {
  const theme = useTheme();

  /**
   * Handle tab change with animation
   */
  const handleChange = (event, newValue) => {
    if (onChange) {
      onChange(newValue);
    }
  };

  /**
   * Animation variants for tab content
   */
  const tabVariants = {
    hidden: { 
      opacity: 0, 
      y: 10,
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 25,
        duration: 0.3
      }
    },
    exit: { 
      opacity: 0, 
      y: -10,
      scale: 0.95,
      transition: {
        duration: 0.2
      }
    }
  };

  /**
   * Custom tab indicator animation
   */
  const indicatorVariants = {
    hidden: { scaleX: 0 },
    visible: { 
      scaleX: 1,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 30
      }
    }
  };

  return (
    <Box sx={{ position: 'relative', ...sx }}>
      <Tabs
        value={value}
        onChange={handleChange}
        variant={variant}
        textColor={color}
        indicatorColor={color}
        size={size}
      sx={{
          '& .MuiTabs-root': {
            minHeight: size === 'small' ? 36 : size === 'large' ? 56 : 48,
          },
          '& .MuiTab-root': {
            minHeight: size === 'small' ? 36 : size === 'large' ? 56 : 48,
            textTransform: 'none',
            fontWeight: 500,
            fontSize: size === 'small' ? '0.875rem' : size === 'large' ? '1rem' : '0.9375rem',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '8px 8px 0 0',
            margin: '0 2px',
            '&:hover': {
              backgroundColor: alpha(theme.palette[color].main, 0.08),
              transform: 'translateY(-1px)',
            },
            '&.Mui-selected': {
              color: theme.palette[color].main,
              fontWeight: 600,
              backgroundColor: alpha(theme.palette[color].main, 0.05),
              '&:hover': {
                backgroundColor: alpha(theme.palette[color].main, 0.12),
              }
            },
            '& .MuiTab-iconWrapper': {
              marginBottom: size === 'small' ? 2 : 4,
              transition: 'transform 0.3s ease',
            },
            '&:hover .MuiTab-iconWrapper': {
              transform: 'scale(1.1)',
            },
            '&.Mui-selected .MuiTab-iconWrapper': {
              transform: 'scale(1.05)',
              color: theme.palette[color].main,
            }
          },
          '& .MuiTabs-indicator': {
            height: 3,
            borderRadius: '3px 3px 0 0',
            background: `linear-gradient(90deg, ${theme.palette[color].main}, ${theme.palette[color].light})`,
            boxShadow: `0 0 8px ${alpha(theme.palette[color].main, 0.4)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          },
          '& .MuiTabs-flexContainer': {
            gap: 1,
          },
          '& .MuiTabs-scroller': {
            '&::-webkit-scrollbar': {
              height: 4,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: alpha(theme.palette.grey[300], 0.3),
              borderRadius: 2,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(theme.palette[color].main, 0.3),
              borderRadius: 2,
              '&:hover': {
                backgroundColor: alpha(theme.palette[color].main, 0.5),
              }
            }
          }
        }}
        {...props}
      >
        <AnimatePresence mode="wait">
      {tabs.map((tab, index) => (
          <motion.div
              key={`tab-${index}`}
            variants={tabVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              style={{ display: 'inline-block' }}
            >
              <Tab
                label={tab.label}
                icon={tab.icon}
                iconPosition={tab.iconPosition || 'start'}
                disabled={tab.disabled || false}
                sx={{ 
                  '& .MuiSvgIcon-root': {
                    fontSize: size === 'small' ? '1rem' : size === 'large' ? '1.5rem' : '1.25rem',
                  }
                }}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </Tabs>

      {/* Custom glow effect for active tab */}
      <motion.div
        variants={indicatorVariants}
        initial="hidden"
        animate="visible"
        style={{
                    position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 1,
          background: `linear-gradient(90deg, transparent, ${alpha(theme.palette[color].main, 0.2)}, transparent)`,
          pointerEvents: 'none',
        }}
                />
    </Box>
  );
};

export default AnimatedTabs; 